import React, { Fragment, useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from "react-datepicker"
import { format } from 'date-fns'

import FilterSearch from 'pages/parts/FilterSearch'
import Pagination, { ItemPerPage } from 'pages/parts/Pagination'

import { GET, PATCH, PGET } from 'helpers/api'
import { formatDate, formatDateBackend, findFromArrayById } from 'helpers/helpers'

import { CURRENCY_UZS, currencyOf, formatSum, toAmount } from './shiftClose/shiftCloseCalc'
import SafeAcceptPanel from './SafeAcceptPanel'

const URL = '/services/web/api/safe-deposit'

const EMPTY_FILTER = { startDate: null, endDate: null, posId: null }

// status -> i18n key; pill colors reuse .tp-status-pending / .tp-status-open
const SAFE_STATUSES = {
	pending: { i18n: 'WAITING', className: 'pending' },
	accepted: { i18n: 'ACCEPTED_STATUS', className: 'open' },
}
const STATUS_CHIPS = [null, 'pending', 'accepted']

// backend status -> SAFE_STATUSES key; backend sends "APPROVED" for an accepted deposit
const STATUS_ALIASES = { approved: 'accepted' }

function getStatus(item) {
	const status = item.status?.toLowerCase()
	const key = STATUS_ALIASES[status] || status
	return SAFE_STATUSES[key] ? key : 'pending'
}

function formatDateTime(value) {
	return value ? format(new Date(value), 'dd.MM.yyyy HH:mm') : ''
}

// safeDepositAmounts -> [[currency, amount]] summed per currency, UZS first
function sumByCurrency(amounts, field) {
	const totals = {}
	amounts.forEach(amount => {
		const currency = currencyOf(amount)
		totals[currency] = (totals[currency] || 0) + toAmount(amount[field])
	})
	return Object.entries(totals).sort(([a], [b]) => (b === CURRENCY_UZS) - (a === CURRENCY_UZS))
}

function renderSums(totals, className = '') {
	return (
		<div className="tp-balance">
			{totals.map(([currency, amount]) => {
				const [value, sign] = formatSum(amount, currency).split(/ (?=\S+$)/)
				return (
					<Fragment key={currency}>
						<span className={`tp-balance-amount ${className}`}>{value}</span>
						<span className="tp-balance-currency">{sign}</span>
					</Fragment>
				)
			})}
		</div>
	)
}

function ShiftsTab({ onPendingChange }) {
	const { t } = useTranslation()

	const [data, setData] = useState([])
	const [total, setTotal] = useState(0)
	const [poses, setPoses] = useState([])
	const [search, setSearch] = useState('')
	const [status, setStatus] = useState(null)
	const [filter, setFilter] = useState(EMPTY_FILTER)
	const [filterDraft, setFilterDraft] = useState(EMPTY_FILTER)
	const [filterOpen, setFilterOpen] = useState(false)
	const [page, setPage] = useState(0)
	const [size, setSize] = useState(20)
	const [panelItem, setPanelItem] = useState(null)

	const posOptions = poses.map(pos => ({ value: pos.id, label: pos.name }))

	// backend has no search / status params: both filter the loaded page only
	const query = search.trim().toLowerCase()
	const searchedData = data.filter(item =>
		!query || [item.cashboxName, item.posName, item.senderName, item.shiftNumber].some(value => String(value ?? '').toLowerCase().includes(query))
	)
	const counts = {
		all: searchedData.length,
		pending: searchedData.filter(item => getStatus(item) === 'pending').length,
		accepted: searchedData.filter(item => getStatus(item) === 'accepted').length,
	}
	const pageData = searchedData.filter(item => !status || getStatus(item) === status)
	const pageCount = Math.ceil(total / size)

	async function getData() {
		const response = await PGET(URL, {
			posId: filter.posId,
			startDate: formatDateBackend(filter.startDate),
			endDate: formatDateBackend(filter.endDate),
			page,
			size,
		}, { loader: true })
		if (response && response.data) {
			setData(response.data)
			setTotal(Number(response.headers['x-total-count']) || 0)
			// tab badge: pending of the loaded page (no status param for a total count)
			onPendingChange?.(response.data.filter(item => getStatus(item) === 'pending').length)
		}
	}

	async function getPoses() {
		const response = await GET('/services/web/api/pos-helper')
		if (response) {
			setPoses(response)
		}
	}

	function toggleFilter(isOpen) {
		if (isOpen) setFilterDraft(filter)
		setFilterOpen(isOpen)
	}

	function applyFilter(newFilter) {
		setFilter(newFilter)
		setPage(0)
		setFilterOpen(false)
	}

	function resetFilter() {
		setSearch('')
		applyFilter(EMPTY_FILTER)
	}

	// details = [{ detailId, acceptedAmount }] from SafeAcceptPanel; on success the list is reloaded
	async function accept(id, details) {
		const response = await PATCH(`/services/web/api/update-safe-deposit/${id}/acceptance-details`, {
			safeTransferId: id,
			safeTransferAmountDetails: details,
		}, { loader: true })
		if (response) {
			setPanelItem(null)
			getData()
		}
	}

	// difference = accepted - sent, same as in SafeAcceptPanel
	function renderDifference(item) {
		if (getStatus(item) === 'pending') return <span className="tp-muted">—</span>
		const amounts = item.safeDepositAmounts || []
		const sent = sumByCurrency(amounts, 'sentAmount')
		return sumByCurrency(amounts, 'acceptedAmount').map(([currency, accepted]) => {
			const amount = accepted - (sent.find(([c]) => c === currency)?.[1] || 0)
			return (
				<div key={currency} className={amount < 0 ? 'tp-negative' : ''}>
					{amount < 0 ? '- ' + formatSum(-amount, currency) : formatSum(amount, currency)}
				</div>
			)
		})
	}

	useEffect(() => {
		getPoses()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		getData()
	}, [filter, page, size]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="d-flex mb-3">
				<div>
					{/* search is local (backend has no search param), FilterSearch only gives the standard input */}
					<FilterSearch
						filterData={{ search }}
						setFilterData={(update) => setSearch(update({}).search)}
						disableGroupSearch>
					</FilterSearch>
				</div>
				<div>
					<Dropdown show={filterOpen} onToggle={toggleFilter} autoClose="outside">
						<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100 me-2">
							{t('FILTER')}
						</Dropdown.Toggle>
						<Dropdown.Menu className="dropdown-filter">
							<div className="row">
								<div className="form-group col-md-6">
									<label>{t('DATE_FROM')}</label>
									<div className="calendar-input">
										<DatePicker
											className="form-control"
											dateFormat="dd.MM.yyyy"
											selected={filterDraft.startDate}
											onChange={(date) => setFilterDraft({ ...filterDraft, startDate: date })}
										/>
										<i className="uil uil-calendar-alt"></i>
									</div>
								</div>
								<div className="form-group col-md-6">
									<label>{t('DATE_TO')}</label>
									<div className="calendar-input">
										<DatePicker
											className="form-control"
											dateFormat="dd.MM.yyyy"
											selected={filterDraft.endDate}
											onChange={(date) => setFilterDraft({ ...filterDraft, endDate: date })}
											minDate={filterDraft.startDate}
										/>
										<i className="uil uil-calendar-alt"></i>
									</div>
								</div>
								<div className="form-group col-md-12">
									<label>{t('TRADING_POINT')}</label>
									<Select
										options={posOptions}
										value={posOptions.find(option => option.value === filterDraft.posId) || null}
										onChange={(option) => setFilterDraft({ ...filterDraft, posId: option?.value || null })}
										isClearable
										placeholder={t('ALL')}
										noOptionsMessage={() => t('LIST_IS_EMPTY')}
									/>
								</div>
							</div>
							<div className="row mt-3">
								<div className="col-md-12">
									<div className="d-flex justify-content-end">
										<button type="button" className="btn btn-secondary me-2" onClick={() => setFilterOpen(false)}>{t('CANCEL')}</button>
										<button type="button" className="btn btn-primary" onClick={() => applyFilter(filterDraft)}>{t('APPLY')}</button>
									</div>
								</div>
							</div>
						</Dropdown.Menu>
					</Dropdown>
				</div>
			</div>

			{(filter.posId || filter.startDate || filter.endDate || search) &&
				<div className="filter-block mb-3">
					<div className="filter">
						<strong>{t('FILTER')}</strong>
						<br />
						{(filter.startDate || filter.endDate) &&
							<span className="me-2">
								<b>{t('PERIOD')}: </b>
								{filter.startDate && formatDate(filter.startDate)}
								{' - '}
								{filter.endDate && formatDate(filter.endDate)}
							</span>
						}
						{filter.posId &&
							<span className="me-2">
								<b>{t('TRADING_POINT')}: </b> {findFromArrayById(poses, filter.posId)}
							</span>
						}
						{search &&
							<span className="me-2">
								<b>{t('SEARCH')}: </b>
								<span>{search}</span>
							</span>
						}
					</div>
					<div className="vertical-center">
						<button type="button" className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('RESET')}</button>
					</div>
				</div>
			}

			<div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
				<div className="tp-chips">
					{STATUS_CHIPS.map(chip => (
						<button type="button" key={chip || 'all'}
							className={'tp-chip' + (status === chip ? ' active' : '')}
							onClick={() => setStatus(chip)}>
							{chip ? t(SAFE_STATUSES[chip].i18n) : t('ALL')}
							<span className="tp-chip-count">{counts[chip || 'all']}</span>
						</button>
					))}
				</div>
				<ItemPerPage itemsPerPage={size} setItemsPerPage={(value) => { setSize(value); setPage(0) }} />
			</div>

			<div className="table-responsive">
				<table className="table table-striped mb-0">
					<thead>
						<tr>
							<th>№</th>
							<th>{t('CLOSE_DATE')}</th>
							<th>{t('SHIFT')}</th>
							<th>{t('TRADING_POINT')}</th>
							<th>{t('CASHBOX')}</th>
							<th>{t('HANDED_BY')}</th>
							<th className="text-end">{t('HANDED')}</th>
							<th className="text-end">{t('ACCEPTED_AMOUNT')}</th>
							<th className="text-end">{t('SC_DIFFERENCE')}</th>
							<th>{t('ACCEPTED_BY')}</th>
							<th>{t('STATUS')}</th>
							<th>{t('ACTION')}</th>
						</tr>
					</thead>
					<tbody>
						{pageData.length > 0 ? pageData.map((item, index) => {
							const isPending = getStatus(item) === 'pending'
							const statusInfo = SAFE_STATUSES[getStatus(item)]
							const amounts = item.safeDepositAmounts || []

							return (
								<tr key={item.safeTransferId}>
									<td>{page * size + index + 1}</td>
									<td className="text-nowrap">{formatDateTime(item.cashboxCloseDate)}</td>
									<td>№{item.shiftNumber}</td>
									<td>{item.posName}</td>
									<td className="tp-name">{item.cashboxName}</td>
									<td>{item.senderName}</td>
									<td className="text-end">{renderSums(sumByCurrency(amounts, 'sentAmount'), 'fw-600')}</td>
									<td className="text-end">
										{isPending ? <span className="tp-muted">—</span> : renderSums(sumByCurrency(amounts, 'acceptedAmount'), 'fw-normal')}
									</td>
									<td className="text-end text-nowrap">{renderDifference(item)}</td>
									<td>{item.receiverName || <span className="tp-muted">—</span>}</td>
									<td>
										<span className={`tp-status tp-status-${statusInfo.className}`}>
											<span className="tp-dot"></span>
											{t(statusInfo.i18n)}
										</span>
									</td>
									<td>
										{isPending ?
											<button type="button" className="btn tp-action-btn tp-action-confirm tp-action-sm" onClick={() => setPanelItem(item)}>
												<i className="uil uil-check"></i>
												{t('ACCEPT')}
											</button>
											:
											<button type="button" className="btn tp-action-btn tp-action-close tp-action-sm" onClick={() => setPanelItem(item)}>
												<i className="uil uil-eye"></i>
												{t('MORE_DETAILS')}
											</button>
										}
									</td>
								</tr>
							)
						}) : <tr>
								<td colSpan="12" className="text-center">{t('NO_DATA')}</td>
							</tr>
						}
					</tbody>
				</table>
			</div>

			<Pagination pageCount={pageCount} paginate={(data) => setPage(data.selected)} reduxPage={page}></Pagination>

			<SafeAcceptPanel
				item={panelItem}
				isPending={!!panelItem && getStatus(panelItem) === 'pending'}
				onHide={() => setPanelItem(null)}
				onAccept={accept}
			/>
		</>
	)
}

export default ShiftsTab
